import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import ComplianceFooter from "../components/ComplianceFooter";

const COVER = "https://images.unsplash.com/photo-1760042770723-5b3324aad901?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpdGFsaWFuJTIwY291bnRyeXNpZGUlMjBkcml2aW5nfGVufDB8fHx8MTc3NzcxMTM4N3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(`Bentornato/a, ${res.user.name || res.user.email}!`);
      const dest = res.user.role === "admin" ? "/admin" : "/dashboard";
      navigate(location.state?.from || dest, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F7F8F9]" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src={COVER} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F7B85]/85 via-[#0F7B85]/50 to-transparent" />
        <div className="absolute top-10 left-10">
          <img src="/brand/besafe-logo.png" alt="Besafe Broker" className="h-16 w-auto drop-shadow-2xl"/>
        </div>
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.2em] opacity-70">Massimo cumulabile</div>
          <div className="font-display text-5xl mt-2">100€ <span className="text-[#FCD9A0] italic font-light text-3xl">di abbuono RCA</span></div>
          <div className="mt-4 text-sm opacity-80 max-w-md">
            Ogni amico che sottoscrive una polizza vale 10€ sulla tua prossima scadenza.
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md fade-up">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0F7B85]">
            ← Torna alla home
          </Link>
          <h1 className="mt-8 font-display text-3xl sm:text-4xl tracking-tight text-[#0F7B85]">
            Accedi al tuo wallet
          </h1>
          <p className="mt-2 text-slate-600">Entra per segnalare un amico e gestire i tuoi abbuoni.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="nome@email.it"
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none transition-all text-stone-900"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="La tua password"
                className="w-full bg-white border border-slate-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#0F7B85] focus:border-transparent outline-none transition-all text-stone-900"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit-button"
              className="w-full bg-[#0F7B85] text-white hover:bg-[#0A5C64] disabled:opacity-60 rounded-lg px-6 py-3 font-medium transition-colors inline-flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <ArrowRight className="w-4 h-4" />}
              Accedi
            </button>
            <div className="text-right">
              <Link
                to="/password-dimenticata"
                data-testid="login-forgot-password-link"
                className="text-xs text-slate-500 hover:text-[#0F7B85] hover:underline"
              >
                Hai dimenticato la password?
              </Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
            <div className="flex-1 h-px bg-slate-200" /> oppure <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            data-testid="login-google-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/dashboard";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full border border-slate-200 bg-white rounded-lg px-6 py-3 font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.61z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A8.99 8.99 0 0 0 9 18z" fill="#34A853"/>
              <path d="M3.97 10.71a5.4 5.4 0 0 1 0-3.42V4.97H.96a9 9 0 0 0 0 8.06l3.01-2.32z" fill="#FBBC05"/>
              <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.97 8.97 0 0 0 9 0 8.99 8.99 0 0 0 .96 4.97l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            Continua con Google
          </button>

          <p className="mt-8 text-sm text-center text-slate-600">
            Non hai un account?{" "}
            <Link to="/registrati" data-testid="link-to-register" className="text-[#0F7B85] font-medium hover:text-[#F39C12]">
              Registrati
            </Link>
          </p>
        </div>
      </div>
      <ComplianceFooter />
    </div>
  );
}
